import {
  IMAGEMOTION_PACKAGES,
  IMAGEMOTION_PAID_PACKAGE_IDS,
  resolveImagemotionPackage,
  recommendImagemotionPackage,
  imagemotionHasStandardFeatures,
  imagemotionHasPremiumFeatures,
  imagemotionIsTester,
  type ImagemotionPackageId,
} from '@rehub/pricing';

/** Legacy JSONB on older images. New projects do not add people. */
export type PersonSettings = Record<string, unknown>;

export type HyperDuskSceneType = 'exterior' | 'interior';

export const SCENE_TYPE_OPTIONS: { value: HyperDuskSceneType; label: string; description: string }[] = [
  { value: 'exterior', label: 'Exterior', description: 'Outside view of the property' },
  { value: 'interior', label: 'Interior', description: 'Inside a room (living room, kitchen, etc.)' },
];

export type ImagemotionStatus =
  | 'queued'
  | 'generating_clips'
  | 'editing'
  | 'rendering'
  | 'completed'
  | 'failed'
  | 'expired';

export type ImagemotionQuality = 'tester' | 'quick' | 'standard' | 'premium' | 'ultra_premium';
export type ImagemotionServiceType = 'standard' | 'hyper_dusk';
export type ImagemotionImageGenerationStatus =
  | 'pending'
  | 'dusk_processing'
  | 'awaiting_user_approval'
  | 'dusk_ready'
  | 'kling_processing'
  | 'animation_ready'
  | 'completed'
  | 'failed';

export interface PackageConfig {
  label: string;
  credits: number;
  maxImages: number;
  description: string;
  badge: string | null;
  features: readonly string[];
  selectable?: boolean;
  isTester?: boolean;
}

/** Active create-flow packages (tester shown separately). */
export const PACKAGES: Record<'quick' | 'standard' | 'premium', PackageConfig> = {
  quick: IMAGEMOTION_PACKAGES.quick,
  standard: IMAGEMOTION_PACKAGES.standard,
  premium: IMAGEMOTION_PACKAGES.premium,
};

/** Full catalogue including tester + legacy for display / resolution. */
export const ALL_PACKAGES: Record<ImagemotionQuality, PackageConfig> = {
  tester: IMAGEMOTION_PACKAGES.tester,
  quick: IMAGEMOTION_PACKAGES.quick,
  standard: IMAGEMOTION_PACKAGES.standard,
  premium: IMAGEMOTION_PACKAGES.premium,
  ultra_premium: IMAGEMOTION_PACKAGES.ultra_premium,
};

export const QUALITY_TIERS = ALL_PACKAGES;
export const PAID_PACKAGE_IDS = IMAGEMOTION_PAID_PACKAGE_IDS;

export {
  resolveImagemotionPackage,
  recommendImagemotionPackage,
  imagemotionHasStandardFeatures,
  imagemotionHasPremiumFeatures,
  imagemotionIsTester,
};
export type { ImagemotionPackageId };

export interface ServiceConfig {
  label: string;
  description: string;
  badge?: string | null;
  defaultClipDuration: number;
  additionalCredits: number;
}

export const SERVICES: Record<ImagemotionServiceType, ServiceConfig> = {
  standard: {
    label: 'Standard Motion',
    description: 'Create cinematic motion clips from your listing photos.',
    badge: null,
    defaultClipDuration: 3,
    additionalCredits: 0,
  },
  hyper_dusk: {
    label: 'Hyper Dusk',
    description: 'Transform daylight images into dusk scenes and animate light-to-dark transitions.',
    badge: 'New',
    defaultClipDuration: 5,
    additionalCredits: 20,
  },
};

// --- Project Settings (stored as JSONB) ---

// 16:9 and 9:16 are the user-facing options in the Standard/Premium UI.
// Hyper Dusk runs through Kling 3.0 Pro which also accepts 1:1 as a
// native output ratio, so we allow it here to keep the source geometry
// when a (near) square photo is uploaded.
export type AspectRatio = '16:9' | '9:16' | '1:1';

export interface ProjectSettings {
  // Standard + Premium
  transition_effect?: string;
  aspect_ratio?: AspectRatio;

  // Premium only
  output_resolution?: 'hd';
  clip_duration?: number;
  filter?: string | null;
  intro_text?: string | null;
  outro_text?: string | null;
  title_font_size?: number;
  logo_on_title_cards?: boolean;
  logo_intro_card?: boolean;
  logo_outro_card?: boolean;
  logo_bumper_duration?: number;
  logo_bumper_tagline?: string | null;
  logo_bumper_animation?: string;
  logo_position?: string;
  logo_scale?: number;
  logo_opacity?: number;
  music_volume?: number;
  music_effect?: string;
  background_color?: string;

  /** Set when created from a Photoplan job so the finished video can return. */
  photoplan_booking_id?: string;
  photoplan_job_id?: string;
  photoplan_return_url?: string;
  photoplan_asset_ids?: string[];
  photoplan_deployed_at?: string;
  photoplan_deliverable_file_id?: string;
}

export const NO_PEOPLE_IN_REFLECTIONS_GUARD =
  'This is a vacant real-estate listing — the property is unoccupied and nobody is filming. ' +
  'Do NOT add people, human figures, faces, silhouettes, photographers, videographers, or camera operators anywhere — including in mirrors, glass, windows, shower screens, TV screens, splashbacks, doorways, or shadows. ' +
  'Never show anyone in glass or mirrors pretending to be the cameraman, including a reflected person holding a camera, phone, or gimbal, or facing the scene as if they are filming it. ' +
  'All reflective surfaces must remain exactly as they appear in the original image, with no added people or cameras.';

export const DEFAULT_MOTION_PROMPT =
  'Smooth cinematic dolly forward through the scene with pronounced natural parallax between foreground furniture or landscaping, midground architecture and the background. ' +
  'The building, furniture and architectural elements remain completely stable and structurally consistent. ' +
  'Camera motion is the primary source of movement — a real virtual camera travelling through 3D space, not a still with animated plants. ' +
  'Very subtle natural foliage movement only. No warping, morphing, furniture movement or artificial object animation. ' +
  'Keep EVERYTHING in the scene exactly as shown in the original photograph. ' +
  'Do NOT add, generate, invent, or hallucinate any objects, people, animals, furniture, decorations, text, or extra architecture. ' +
  NO_PEOPLE_IN_REFLECTIONS_GUARD + ' ' +
  'Mirrors and glass must stay empty — no person, face, silhouette, photographer, or camera in any reflection. ' +
  'Do NOT change, move, remove, warp, or alter any existing objects in the scene. ' +
  'Photorealistic listing footage that could not be reproduced by simply zooming a still photograph.';

export const DEFAULT_HYPER_DUSK_NANO_PROMPT =
  'Create a high-end twilight version of this property photo for real-estate marketing. ' +
  'Preserve architecture, geometry, and camera perspective exactly as the input image. ' +
  'Shift the sky and ambient lighting naturally from daylight to blue-hour dusk with subtle warm interior window glow. ' +
  'Do not add or remove objects, text, people, cars, or furniture. ' +
  'Keep the output photorealistic, clean, and true to the original listing.';

export const DEFAULT_HYPER_DUSK_NANO_PROMPT_INTERIOR =
  'Create a high-end evening version of this interior property photo for real-estate marketing. ' +
  'This is an INTERIOR photo — do NOT paint sky, sunset, or dusk colors onto the ceiling, walls, or any indoor surface. ' +
  'Ceilings must remain their original color and material. Walls must remain their original color. ' +
  'Only modify the view THROUGH windows to show a twilight/dusk sky outside. ' +
  'Darken the overall ambient lighting to clearly look like evening. ' +
  'Add warm, inviting lamp and fixture glow from existing light sources in the room. ' +
  'Preserve the interior layout, furniture, decor, geometry, and camera perspective exactly as the input image. ' +
  'Do not add or remove objects, text, people, or furniture. ' +
  'Keep the output photorealistic, clean, and true to the original listing.';

export const DEFAULT_HYPER_DUSK_MOTION_PROMPT =
  'Create a smooth cinematic animation that transitions naturally from daylight to dusk. ' +
  'Smooth dolly forward toward the property with pronounced natural parallax between foreground, facade and sky. ' +
  'Architecture remains structurally fixed — camera travel is the primary motion, not foliage or cloud animation. ' +
  'Keep composition, structure, and all objects unchanged from the source image. ' +
  'Do not add or hallucinate people, vehicles, text, or extra objects. ' +
  NO_PEOPLE_IN_REFLECTIONS_GUARD + ' ' +
  'Maintain photoreal quality with realistic evening ambience and gradual darkening.';

export const DEFAULT_HYPER_DUSK_MOTION_PROMPT_INTERIOR =
  'Create a smooth cinematic animation of an interior room transitioning to evening. ' +
  'The view through windows gradually shifts from daylight to dusk. Interior lamps brighten warmly. ' +
  'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
  'Smooth dolly forward through the room with natural parallax on furniture, door frames and walls. ' +
  'Architecture and furniture remain structurally fixed. ' +
  'Keep composition, structure, and all objects unchanged from the source image. ' +
  'Do not add or hallucinate people, vehicles, text, or extra objects. ' +
  NO_PEOPLE_IN_REFLECTIONS_GUARD + ' ' +
  'Maintain photoreal quality with realistic evening ambience.';

export interface HyperDuskVariant {
  id: string;
  path: string;
  url?: string | null;
  created_at: string;
  preset_id?: string | null;
  animation_style?: HyperDuskAnimationStyle | null;
  nano_request_id?: string | null;
}

export type HyperDuskAnimationStyle = 'static' | 'gentle_dolly' | 'slow_push_in' | 'cinematic_pan' | 'drone_reveal';

export interface HyperDuskAnimationStyleOption {
  value: HyperDuskAnimationStyle;
  label: string;
  description: string;
}

export const HYPER_DUSK_ANIMATION_STYLES: HyperDuskAnimationStyleOption[] = [
  { value: 'static', label: 'Static', description: 'No camera movement — the shot stays perfectly still while the scene transitions to dusk.' },
  { value: 'gentle_dolly', label: 'Gentle Dolly', description: 'Very stable cinematic drift with subtle parallax.' },
  { value: 'slow_push_in', label: 'Slow Push In', description: 'Refined forward movement to spotlight architecture.' },
  { value: 'cinematic_pan', label: 'Cinematic Pan', description: 'Horizontal movement for dramatic facade reveals.' },
  { value: 'drone_reveal', label: 'Drone Reveal', description: 'Aerial-inspired motion with premium listing energy.' },
];

export const DEFAULT_HYPER_DUSK_ANIMATION_STYLE: HyperDuskAnimationStyle = 'gentle_dolly';

export interface HyperDuskPreset {
  id: string;
  label: string;
  description: string;
  nanoPrompt: string;
  nanoPromptInterior: string;
  klingPromptBase: string;
  klingPromptBaseInterior: string;
  defaultAnimationStyle: HyperDuskAnimationStyle;
}

export const HYPER_DUSK_PRESETS: HyperDuskPreset[] = [
  {
    id: 'cinematic_twilight',
    label: 'Cinematic Twilight',
    description: 'Luxury twilight transformation with warm interior glow.',
    nanoPrompt: DEFAULT_HYPER_DUSK_NANO_PROMPT,
    nanoPromptInterior: DEFAULT_HYPER_DUSK_NANO_PROMPT_INTERIOR,
    klingPromptBase: DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    klingPromptBaseInterior: DEFAULT_HYPER_DUSK_MOTION_PROMPT_INTERIOR,
    defaultAnimationStyle: 'gentle_dolly',
  },
  {
    id: 'twilight_lights_on',
    label: 'Twilight Lights On',
    description: 'Turns on interior/exterior building lights for a richer dusk mood.',
    nanoPrompt:
      'Create a premium twilight version of this property and ensure realistic architectural lighting is switched on. ' +
      'Illuminate visible interior rooms through windows with warm, natural light and activate tasteful exterior fixtures when present. ' +
      'Preserve architecture, perspective, and all objects exactly as-is. ' +
      'Do not add or remove people, cars, furniture, text, or any new elements.',
    nanoPromptInterior:
      'Create a premium evening version of this interior room with all lighting switched on. ' +
      'This is an INTERIOR photo — do NOT paint sky, sunset, or dusk colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a twilight sky outside. ' +
      'Turn on all visible lamps, pendants, sconces, and recessed lighting with warm golden glow. ' +
      'Preserve the interior layout, furniture, decor, and all objects exactly as-is. ' +
      'Do not add or remove people, furniture, text, or any new elements.',
    klingPromptBase:
      'Generate a smooth cinematic transition from daylight to twilight where building lights gradually come alive. ' +
      'Keep the scene photoreal and structurally identical to the source image.',
    klingPromptBaseInterior:
      'Generate a smooth cinematic transition of an interior room to evening where room lights gradually come alive. ' +
      'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Keep the scene photoreal and structurally identical to the source image.',
    defaultAnimationStyle: 'slow_push_in',
  },
  {
    id: 'moody_blue_hour',
    label: 'Moody Blue Hour',
    description: 'Cool, dramatic blue-hour mood with crisp exterior contrast.',
    nanoPrompt:
      'Transform this listing image into dramatic blue-hour dusk with deep cool shadows and crisp highlights. ' +
      'Preserve architecture, geometry, and all objects exactly as captured. ' +
      'Do not add people, cars, furniture, text, or synthetic elements.',
    nanoPromptInterior:
      'Transform this interior listing photo into a moody blue-hour evening scene. ' +
      'This is an INTERIOR photo — do NOT paint blue-hour colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a deep blue evening sky. ' +
      'Darken the room and add warm lamp glow as contrast. ' +
      'Preserve the interior layout, furniture, and all objects exactly as captured. ' +
      'Do not add people, furniture, text, or synthetic elements.',
    klingPromptBase:
      'Create a smooth cinematic transition from daylight to dramatic blue-hour dusk. ' +
      'Maintain exact composition and photoreal detail with premium real-estate style.',
    klingPromptBaseInterior:
      'Create a smooth cinematic transition of an interior room to a moody blue-hour evening. ' +
      'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Maintain exact composition and photoreal detail with premium real-estate style.',
    defaultAnimationStyle: 'cinematic_pan',
  },
  {
    id: 'warm_luxury_glow',
    label: 'Warm Luxury Glow',
    description: 'High-end evening ambience with rich warm window lighting.',
    nanoPrompt:
      'Create an elegant luxury dusk version with warm window glow, balanced sky gradients, and natural evening ambiance. ' +
      'Keep the exact scene composition and all visible objects unchanged. ' +
      'No people, text, or hallucinated additions.',
    nanoPromptInterior:
      'Create an elegant luxury evening version of this interior room with warm lamp glow and cozy ambiance. ' +
      'This is an INTERIOR photo — do NOT paint sky, sunset, or dusk colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show an evening sky. ' +
      'Keep the exact interior composition and all visible objects unchanged. ' +
      'No people, text, or hallucinated additions.',
    klingPromptBase:
      'Generate a premium evening transition that enhances warm architectural lighting while preserving realism and scene fidelity.',
    klingPromptBaseInterior:
      'Generate a premium evening transition of an interior room that enhances warm room lighting. ' +
      'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Preserve realism and scene fidelity.',
    defaultAnimationStyle: 'slow_push_in',
  },
];

export const DEFAULT_HYPER_DUSK_PRESET_ID = HYPER_DUSK_PRESETS[0].id;

export const ASPECT_RATIOS = [
  { value: '16:9' as AspectRatio, label: 'Landscape', description: '16:9', icon: '▬' },
  { value: '9:16' as AspectRatio, label: 'Portrait', description: '9:16', icon: '▮' },
] as const;

export const DEFAULT_SETTINGS: ProjectSettings = {
  transition_effect: 'dissolve',
  aspect_ratio: '16:9',
  output_resolution: 'hd',
  clip_duration: 5,
  filter: null,
  intro_text: null,
  outro_text: null,
  title_font_size: 64,
  logo_on_title_cards: false,
  logo_intro_card: false,
  logo_outro_card: false,
  logo_bumper_duration: 3,
  logo_bumper_tagline: null,
  logo_bumper_animation: 'fadeIn',
  logo_position: 'bottomRight',
  logo_scale: 0.3,
  logo_opacity: 0.8,
  music_volume: 0.7,
  music_effect: 'fadeInFadeOut',
  background_color: '#000000',
};

export const TITLE_FONT_SIZES = [
  { value: 48, label: 'Small' },
  { value: 64, label: 'Medium' },
  { value: 80, label: 'Large' },
  { value: 100, label: 'Extra Large' },
] as const;

// --- Option constants ---

export const STANDARD_TRANSITIONS = [
  { value: 'dissolve', label: 'Dissolve (Cross-fade)' },
  { value: 'fade', label: 'Fade to Black' },
  { value: 'none', label: 'No Transition (Cut)' },
] as const;

export const PREMIUM_TRANSITIONS = [
  ...STANDARD_TRANSITIONS,
  { value: 'slideLeft', label: 'Slide Left' },
  { value: 'slideRight', label: 'Slide Right' },
  { value: 'slideUp', label: 'Slide Up' },
  { value: 'slideDown', label: 'Slide Down' },
  { value: 'carouselLeft', label: 'Carousel Left' },
  { value: 'carouselRight', label: 'Carousel Right' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'wipeLeft', label: 'Wipe Left' },
  { value: 'wipeRight', label: 'Wipe Right' },
  { value: 'reveal', label: 'Reveal' },
] as const;

export const FILTERS = [
  { value: '', label: 'None' },
  { value: 'boost', label: 'Boost' },
  { value: 'contrast', label: 'Contrast' },
  { value: 'darken', label: 'Darken' },
  { value: 'greyscale', label: 'Greyscale' },
  { value: 'lighten', label: 'Lighten' },
  { value: 'muted', label: 'Muted' },
] as const;

export const LOGO_BUMPER_DURATIONS = [
  { value: 2, label: '2s' },
  { value: 3, label: '3s' },
  { value: 4, label: '4s' },
  { value: 5, label: '5s' },
] as const;

export const LOGO_BUMPER_ANIMATIONS = [
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'slideUp', label: 'Slide Up' },
  { value: 'none', label: 'No Animation' },
] as const;

export const LOGO_POSITIONS = [
  { value: 'topLeft', label: 'Top Left' },
  { value: 'top', label: 'Top' },
  { value: 'topRight', label: 'Top Right' },
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
  { value: 'bottomLeft', label: 'Bottom Left' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'bottomRight', label: 'Bottom Right' },
] as const;

export const MUSIC_EFFECTS = [
  { value: 'fadeInFadeOut', label: 'Fade In & Out' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeOut', label: 'Fade Out' },
  { value: 'none', label: 'No Effect' },
] as const;

export const RESOLUTIONS = [
  { value: 'hd', label: 'Composed 1080p', description: 'Final timeline 1920 × 1080' },
] as const;

// --- Referral types ---

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

// --- Database row types ---

export interface ImagemotionProject {
  id: string;
  user_id: string;
  name: string | null;
  status: ImagemotionStatus;
  quality: ImagemotionQuality;
  service_type?: ImagemotionServiceType;
  settings: ProjectSettings;
  error_message: string | null;
  music_url: string | null;
  logo_url: string | null;
  transition_type: string;
  final_video_url: string | null;
  final_video_path: string | null;
  share_token: string | null;
  is_promoted: boolean;
  brand_profile_id: string | null;
  shotstack_render_id: string | null;
  /** Customer-facing motion quality tier (no fal endpoint on client). */
  motion_tier_id?: string | null;
  motion_tier_version_id?: string | null;
  /** Immutable server snapshot — may be present but must never be rendered to customers. */
  generation_config_snapshot?: Record<string, unknown> | null;
  credits_charged?: number;
  edit_state?: Record<string, unknown> | null;
  edit_revision?: number | null;
  last_rendered_edit_revision?: number | null;
  render_idempotency_key?: string | null;
  previous_final_video_url?: string | null;
  previous_final_video_path?: string | null;
  created_at: string;
  updated_at: string;
  ai_classification?: 'AI_ASSISTED' | 'AI_MODIFIED' | 'AI_GENERATED' | null;
  ai_operation?: string | null;
  ai_created_at?: string | null;
  visible_disclosure_applied?: boolean | null;
}

export interface ImagemotionProjectImage {
  id: string;
  project_id: string;
  image_url: string;
  image_path: string | null;
  order_index: number;
  clip_url: string | null;
  clip_path?: string | null;
  clip_duration: number;
  pending_clip_url?: string | null;
  pending_clip_path?: string | null;
  pending_clip_duration?: number | null;
  pending_generation_id?: string | null;
  clip_versions?: Array<{
    id: string;
    clipUrl: string;
    clipPath: string | null;
    duration: number;
    createdAt: string;
    instructions?: string | null;
    generationId?: string | null;
  }> | null;
  motion_prompt: string | null;
  source_image_url?: string | null;
  source_image_path?: string | null;
  dusk_image_url?: string | null;
  dusk_image_path?: string | null;
  dusk_variants?: HyperDuskVariant[] | null;
  selected_dusk_variant_id?: string | null;
  nano_banana_request_id?: string | null;
  kling_request_id?: string | null;
  kling_animation_style?: HyperDuskAnimationStyle | null;
  ai_classification?: 'AI_ASSISTED' | 'AI_MODIFIED' | 'AI_GENERATED' | null;
  ai_operation?: string | null;
  generation_status?: ImagemotionImageGenerationStatus | null;
  generation_error?: string | null;
  person_settings?: PersonSettings | null;
  generation_id?: string | null;
  created_at: string;
}
